import React from 'react'
import { Box, Typography } from '@mui/material'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../../constants/colors'
import { GlassLabel } from '../GlassLabel'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'

export interface QuoteVisualizationProps {
  /**
   * Quote text to display
   */
  text: string
  /**
   * Optional speaker/source of the quote
   */
  speaker?: string
  /**
   * Variant for team or individual styling
   */
  variant?: 'team' | 'individual'
  /**
   * Additional data for dynamic label generation (e.g., teamMadePlan, teamFollowedPlan, teamEvaluatedPlan)
   */
  additionalData?: Record<string, unknown>
}

/**
 * Helper function to determine the label text based on boolean flags
 */
const getSmartLabel = (
  additionalData: Record<string, unknown> | undefined,
  t: (key: string) => string
): string | null => {
  if (!additionalData) {
    return null
  }

  // Check for teamMadePlan
  if ('teamMadePlan' in additionalData) {
    return additionalData.teamMadePlan
      ? t('gamer.feedback.quoteLabels.teamMadePlan')
      : t('gamer.feedback.quoteLabels.teamDidntMakePlan')
  }

  // Check for teamFollowedPlan
  if ('teamFollowedPlan' in additionalData) {
    return additionalData.teamFollowedPlan
      ? t('gamer.feedback.quoteLabels.teamFollowedPlan')
      : t('gamer.feedback.quoteLabels.teamDidntFollowPlan')
  }

  // Check for teamEvaluatedPlan
  if ('teamEvaluatedPlan' in additionalData) {
    return additionalData.teamEvaluatedPlan
      ? t('gamer.feedback.quoteLabels.teamEvaluatedPlan')
      : t('gamer.feedback.quoteLabels.teamDidntEvaluatePlan')
  }

  return null
}

/**
 * QuoteVisualization - Display quote with glowing background and image
 *
 * Features:
 * - Radial gradient background with glassmorphism
 * - Background glow effects (orange blur)
 * - Title and quote text
 * - Decorative dots
 * - Quote image
 * - Glass label at bottom (dynamic based on boolean flags or speaker)
 */
export const QuoteVisualization: React.FC<QuoteVisualizationProps> = ({
  text,
  speaker,
  variant: _variant, // Reserved for future background color variations
  additionalData
}) => {
  const { t } = useTranslation()

  // Determine label to display
  const smartLabel = getSmartLabel(additionalData, t)
  const labelToShow = smartLabel || speaker
  return (
    <Box
      sx={{
        // Outer container
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '24px',
        gap: '24px',
        isolation: 'isolate',
        position: 'relative',
        width: '100%',
        height: '277px',
        background: `radial-gradient(50.19% 50.19% at 50% 50%, ${INTERAX_COLORS.white20} 0%, rgba(255, 255, 255, 0) 100%), rgba(33, 38, 41, 0.4)`,
        backgroundBlendMode: 'soft-light, normal',
        border: `1px solid ${INTERAX_COLORS.white04}`,
        boxShadow: `inset 0px 1px 0px rgba(255, 255, 255, 0.08)`,
        borderRadius: '10px',
        flex: 'none',
        overflow: 'hidden'
      }}
    >
      {/* Background glow elements */}
      <Box
        sx={{
          position: 'absolute',
          width: '361px',
          height: '222.45px',
          left: '-27px',
          top: '257px',
          zIndex: 0
        }}
      >
        {/* Orange blur */}
        <Box
          sx={{
            position: 'absolute',
            width: '244px',
            height: '177px',
            left: '47px',
            top: '45.45px',
            background: INTERAX_COLORS.sun,
            filter: 'blur(75px)'
          }}
        />
      </Box>

      {/* Content stack */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '0px',
          gap: '16px',
          isolation: 'isolate',
          width: '100%',
          flex: 'none',
          alignSelf: 'stretch',
          zIndex: 1
        }}
      >
        {/* Title: A Quote from your Session */}
        <Typography
          sx={{
            fontFamily: 'Lexend Deca',
            fontStyle: 'normal',
            fontWeight: 600,
            fontSize: '18px',
            lineHeight: '26px',
            color: INTERAX_COLORS.white,
            flex: 'none',
            order: 0,
            flexGrow: 0
          }}
        >
          {t('common.quoteFromSession')}
        </Typography>

        {/* Quote text */}
        <Typography
          sx={{
            ...INTERAX_TYPOGRAPHY.body3,
            color: INTERAX_COLORS.thunderGrey,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {text}
        </Typography>

        {/* Decorative dots */}
        <Box
          sx={{
            position: 'relative',
            width: '11px',
            height: '4px',
            flex: 'none'
          }}
        >
          {/* Large dot */}
          <Box
            sx={{
              position: 'absolute',
              width: '4px',
              height: '4px',
              left: '0px',
              top: '0px',
              background: INTERAX_COLORS.white,
              borderRadius: '50%'
            }}
          />
          {/* Medium dot */}
          <Box
            sx={{
              position: 'absolute',
              width: '2.67px',
              height: '2.67px',
              left: '5.5px',
              top: '0.67px',
              background: INTERAX_COLORS.white70,
              borderRadius: '50%'
            }}
          />
          {/* Small dot */}
          <Box
            sx={{
              position: 'absolute',
              width: '1.33px',
              height: '1.33px',
              left: '9.66px',
              top: '1.33px',
              background: INTERAX_COLORS.white40,
              borderRadius: '50%'
            }}
          />
        </Box>
      </Box>

      {/* Quote image */}
      <Box
        component="img"
        src="/assets/img/quote_image.png"
        alt="Quote visualization"
        sx={{
          width: '283px',
          height: 'auto',
          maxHeight: '67px',
          objectFit: 'contain',
          flex: 'none',
          zIndex: 2
        }}
      />

      {/* Glass label - show smart label (based on boolean flags) or speaker */}
      {labelToShow && <GlassLabel label={labelToShow} sx={{ zIndex: 3 }} />}
    </Box>
  )
}

export default QuoteVisualization
